/**
 * Live password requirements checklist.
 *
 * Used by the registration page (no auth, falls back to default policy)
 * and the setup-password page (also unauthenticated — invitation token
 * carries the org context). When the org's actual policy can be
 * fetched, the parent passes it via ``policy``; otherwise we show the
 * default-friendly baseline so the user has SOMETHING to aim at.
 *
 * The parent owns ``allValid`` via the ``onValidityChange`` callback
 * so it can disable the submit button until every requirement passes.
 */
import { useEffect, useMemo } from "react";
import { Check, X } from "lucide-react";

export interface PasswordPolicySnapshot {
  min_password_length: number;
  require_uppercase: boolean;
  require_number: boolean;
  require_special: boolean;
}

export const DEFAULT_PASSWORD_POLICY: PasswordPolicySnapshot = {
  min_password_length: 8,
  require_uppercase: true,
  require_number: true,
  require_special: false,
};

const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

interface RequirementCheck {
  label: string;
  passed: boolean;
}

export function evaluatePolicy(
  password: string,
  policy: PasswordPolicySnapshot,
): RequirementCheck[] {
  const checks: RequirementCheck[] = [
    {
      label: `At least ${policy.min_password_length} characters`,
      passed: password.length >= policy.min_password_length,
    },
  ];
  if (policy.require_uppercase) {
    checks.push({
      label: "Contains an uppercase letter",
      passed: /[A-Z]/.test(password),
    });
  }
  if (policy.require_number) {
    checks.push({
      label: "Contains a number",
      passed: /\d/.test(password),
    });
  }
  if (policy.require_special) {
    checks.push({
      label: "Contains a special character",
      passed: SPECIAL_RE.test(password),
    });
  }
  return checks;
}

export function PasswordRequirements({
  password,
  policy = DEFAULT_PASSWORD_POLICY,
  onValidityChange,
}: {
  password: string;
  policy?: PasswordPolicySnapshot;
  onValidityChange?: (allValid: boolean) => void;
}) {
  const checks = useMemo(
    () => evaluatePolicy(password, policy),
    [password, policy],
  );
  const allValid = checks.every((c) => c.passed);

  useEffect(() => {
    onValidityChange?.(allValid);
  }, [allValid, onValidityChange]);

  return (
    <ul className="space-y-1 text-xs">
      {checks.map((c) => (
        <li
          key={c.label}
          className={`flex items-center gap-2 ${
            c.passed ? "text-success" : "text-text-muted"
          }`}
        >
          {c.passed ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <X className="h-3.5 w-3.5 text-danger/60" />
          )}
          <span>{c.label}</span>
        </li>
      ))}
    </ul>
  );
}

export default PasswordRequirements;
